<template>
    <section
        ref="viewport"
        class="workforce-overview"
        :class="{'workforce-overview--pinching': pinchGesture !== null}"
        aria-label="Обзор распределения сотрудников по всем площадкам"
        @dragover.capture="onOverviewDragOver"
        @click.capture="onOverviewClickCapture"
    >
        <div
            ref="board"
            class="workforce-overview__board"
            :style="overviewBoardStyle"
        >
            <section
                v-for="cluster in overviewClusters"
                :key="cluster.key"
                class="workforce-overview__cluster"
            >
                <header class="workforce-overview__cluster-header">
                    <i :class="cluster.icon" aria-hidden="true"></i>
                    <span :title="cluster.title">{{ cluster.title }}</span>
                </header>

                <div class="workforce-overview__columns">
                    <section
                        v-for="column in cluster.columns"
                        :key="column.key"
                        class="workforce-overview__column"
                        :class="{
                            'workforce-overview__column--empty-toggle':
                                column.kind === 'empty-toggle',
                            'workforce-overview__column--planned-object':
                                column.plannedObject === true,
                            'workforce-overview__column--drop-active':
                                employeeDropTargetKey === column.key,
                            'workforce-overview__column--location-dragging':
                                draggingLocationKey === column.locationKey,
                            'workforce-overview__column--drop-before':
                                layoutDropTargetKey === column.locationKey &&
                                layoutDropPosition === 'before',
                            'workforce-overview__column--drop-after':
                                layoutDropTargetKey === column.locationKey &&
                                layoutDropPosition === 'after'
                        }"
                        @dragover="onColumnDragOver($event, column)"
                        @dragleave="onColumnDragLeave($event, column)"
                        @drop="onColumnDrop($event, column)"
                    >
                        <button
                            v-if="column.kind === 'empty-toggle'"
                            type="button"
                            class="workforce-overview__empty-toggle"
                            :aria-expanded="column.expanded ? 'true' : 'false'"
                            @click.stop="toggleEmptyGroupObjects(column.groupId)"
                        >
                            <i
                                class="fas"
                                :class="column.expanded ? 'fa-eye-slash' : 'fa-eye'"
                                aria-hidden="true"
                            ></i>
                            <span>
                                {{ column.expanded ? 'Скрыть пустые' : 'Показать пустые' }}
                            </span>
                            <small>{{ column.emptyCount }}</small>
                        </button>
                        <template v-else>
                        <header class="workforce-overview__column-header">
                            <div
                                v-if="column.location && !mobile"
                                class="workforce-overview__location-title"
                                role="button"
                                tabindex="0"
                                draggable="true"
                                :title="`Перетащить объект «${column.title}»`"
                                @click.stop
                                @dragstart.stop="onLocationDragStart($event, column)"
                                @dragend.stop="$emit('location-dragend')"
                                @keydown="onLocationKeydown($event, column)"
                            >
                                {{ column.title }}
                            </div>
                            <strong v-else :title="column.title">
                                {{ column.title }}
                            </strong>
                            <span class="workforce-overview__counts">
                                <span>{{ column.headcount }}</span>
                                <span
                                    v-if="column.plannedEmployees.length"
                                    class="workforce-overview__planned-count"
                                    :title="`Активных плановых перемещений: ${
                                        column.plannedEmployees.length
                                    }`"
                                >
                                    <i class="far fa-calendar-alt" aria-hidden="true"></i>
                                    +{{ column.plannedEmployees.length }}
                                </span>
                            </span>
                        </header>

                        <div class="workforce-overview__employees">
                            <employee-board-card
                                v-for="employee in column.displayEmployees"
                                :key="employeeKey(employee)"
                                :employee="employee"
                                :can-move="column.canMove && employee.can_move !== false"
                                :mobile="mobile"
                                overview
                                :search-query="searchQuery"
                                :dragging-employee-id="draggingEmployeeId"
                                :dragging-assignment-id="draggingAssignmentId"
                                :planned-move-pending="plannedMovePending"
                                @open="$emit('open', $event)"
                                @contextmenu="forwardContextMenu"
                                @dragstart="$emit('dragstart', $event)"
                                @dragend="$emit('dragend')"
                            ></employee-board-card>
                            <span
                                v-if="!column.displayEmployees.length"
                                class="workforce-overview__empty"
                            >
                                —
                            </span>
                        </div>
                        </template>
                    </section>
                </div>
            </section>
        </div>
    </section>
</template>

<script>
import EmployeeBoardCard from './EmployeeBoardCard.vue';

export default {
    name: 'EmployeeWorkforceOverview',
    components: {
        EmployeeBoardCard
    },
    props: {
        groups: {
            type: Array,
            default: () => []
        },
        standaloneObjects: {
            type: Array,
            default: () => []
        },
        homeEmployees: {
            type: Array,
            default: () => []
        },
        distributionEmployees: {
            type: Array,
            default: () => []
        },
        restEmployees: {
            type: Array,
            default: () => []
        },
        loupeEmployees: {
            type: Array,
            default: () => []
        },
        reserveEmployees: {
            type: Array,
            default: () => []
        },
        plannedObjects: {
            type: Array,
            default: () => []
        },
        plannedEmployeesByTarget: {
            type: Object,
            default: () => ({})
        },
        filterEmployees: {
            type: Function,
            required: true
        },
        objectId: {
            type: Function,
            required: true
        },
        movementAllowed: {
            type: Boolean,
            default: false
        },
        reserveMovementAllowed: {
            type: Boolean,
            default: false
        },
        canMoveAllObjects: {
            type: Boolean,
            default: false
        },
        mobile: {
            type: Boolean,
            default: false
        },
        searchQuery: {
            type: String,
            default: ''
        },
        draggingEmployeeId: {
            type: [String, Number],
            default: null
        },
        draggingAssignmentId: {
            type: [String, Number],
            default: null
        },
        draggingLocationKey: {
            type: String,
            default: null
        },
        layoutDropTargetKey: {
            type: String,
            default: null
        },
        layoutDropPosition: {
            type: String,
            default: 'before'
        },
        plannedMovePending: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            employeeDropTargetKey: null,
            overviewScale: 1,
            pinchGesture: null,
            suppressTapUntil: 0,
            expandedEmptyGroups: {}
        };
    },
    computed: {
        overviewBoardStyle() {
            return {
                zoom: this.overviewScale,
                minHeight: `${100 / this.overviewScale}%`
            };
        },
        overviewClusters() {
            const clusters = [this.commonCluster];
            this.groups.forEach(group => {
                clusters.push(this.groupCluster(group));
            });
            if (this.standaloneObjects.length) {
                clusters.push(this.standaloneCluster);
            }
            if (this.plannedObjects.length) {
                clusters.push(this.plannedObjectCluster);
            }
            return clusters;
        },
        commonCluster() {
            return {
                key: 'common',
                title: 'Общие зоны',
                icon: 'fas fa-th-large',
                columns: [
                    this.column({
                        key: 'home',
                        title: 'Дома / отдых',
                        employees: this.homeEmployees,
                        target: {type: 'home', id: null},
                        canMove: this.movementAllowed,
                        allowDrop: this.canMoveAllObjects
                    }),
                    this.column({
                        key: 'requires-distribution',
                        title: 'Не распределены / выбыли из планирования',
                        employees: this.distributionEmployees,
                        target: {type: 'requires_distribution', id: null},
                        canMove: this.movementAllowed,
                        allowDrop: this.canMoveAllObjects
                    }),
                    this.column({
                        key: 'rest',
                        title: 'Отдых',
                        employees: this.restEmployees,
                        target: null,
                        canMove: false,
                        allowDrop: false
                    }),
                    this.column({
                        key: 'loupe',
                        title: '🔍 Выбыли из планирования',
                        employees: this.loupeEmployees,
                        target: null,
                        canMove: false,
                        allowDrop: false
                    }),
                    this.column({
                        key: 'reserve',
                        title: 'Резерв',
                        employees: this.reserveEmployees,
                        target: null,
                        canMove: this.reserveMovementAllowed,
                        allowDrop: false
                    })
                ]
            };
        },
        standaloneCluster() {
            return {
                key: 'standalone',
                title: 'Без группы',
                icon: 'fas fa-map-marked-alt',
                columns: this.standaloneObjects.map(object => (
                    this.objectColumn(object, 'standalone')
                ))
            };
        },
        plannedObjectCluster() {
            return {
                key: 'planned-objects',
                title: 'Плановые объекты · контракт не заключён',
                icon: 'fas fa-map-signs',
                columns: this.plannedObjects.map(object => this.column({
                    key: `planned-object:${object.planned_object_id}`,
                    title: `${object.planning_code} — ${object.name}`,
                    employees: object.employees,
                    target: null,
                    canMove: false,
                    allowDrop: false,
                    plannedObject: true
                }))
            };
        }
    },
    watch: {
        draggingEmployeeId(value) {
            if (value === null || value === undefined) {
                this.employeeDropTargetKey = null;
            }
        }
    },
    mounted() {
        const viewport = this.$refs.viewport;
        if (!viewport) return;
        viewport.addEventListener(
            'touchstart',
            this.onOverviewTouchStart,
            {passive: false}
        );
        viewport.addEventListener(
            'touchmove',
            this.onOverviewTouchMove,
            {passive: false}
        );
        viewport.addEventListener('touchend', this.onOverviewTouchEnd);
        viewport.addEventListener('touchcancel', this.onOverviewTouchEnd);
    },
    beforeDestroy() {
        const viewport = this.$refs.viewport;
        if (!viewport) return;
        viewport.removeEventListener('touchstart', this.onOverviewTouchStart);
        viewport.removeEventListener('touchmove', this.onOverviewTouchMove);
        viewport.removeEventListener('touchend', this.onOverviewTouchEnd);
        viewport.removeEventListener('touchcancel', this.onOverviewTouchEnd);
    },
    methods: {
        captureViewState() {
            const viewport = this.$refs.viewport;
            return {
                scale: this.overviewScale,
                scrollLeft: viewport ? viewport.scrollLeft : 0,
                scrollTop: viewport ? viewport.scrollTop : 0,
                expandedEmptyGroups: Object.keys(this.expandedEmptyGroups)
                    .filter(key => this.expandedEmptyGroups[key] === true)
            };
        },
        restoreViewState(state) {
            const value = state && typeof state === 'object' ? state : {};
            const scale = Number(value.scale);
            if (Number.isFinite(scale)) {
                this.applyOverviewScale(Math.max(0.5, Math.min(1.5, scale)));
            }
            this.expandedEmptyGroups = (Array.isArray(
                value.expandedEmptyGroups
            ) ? value.expandedEmptyGroups : []).reduce((result, key) => {
                result[String(key)] = true;
                return result;
            }, {});
            this.$nextTick(() => {
                const viewport = this.$refs.viewport;
                if (!viewport) return;
                viewport.scrollLeft = Math.max(0, Number(value.scrollLeft) || 0);
                viewport.scrollTop = Math.max(0, Number(value.scrollTop) || 0);
            });
        },
        pinchDistance(touches) {
            const deltaX = touches[1].clientX - touches[0].clientX;
            const deltaY = touches[1].clientY - touches[0].clientY;
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        },
        pinchPoint(touches, viewport) {
            const rect = viewport.getBoundingClientRect();
            return {
                x: (
                    touches[0].clientX + touches[1].clientX
                ) / 2 - rect.left,
                y: (
                    touches[0].clientY + touches[1].clientY
                ) / 2 - rect.top
            };
        },
        onOverviewTouchStart(event) {
            if (!event.touches || event.touches.length < 2) {
                return;
            }
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            const distance = this.pinchDistance(event.touches);
            if (!distance) return;
            const point = this.pinchPoint(event.touches, viewport);
            this.pinchGesture = {
                startDistance: distance,
                startScale: this.overviewScale,
                anchorX: (viewport.scrollLeft + point.x) / this.overviewScale,
                anchorY: (viewport.scrollTop + point.y) / this.overviewScale
            };
            if (event.cancelable) event.preventDefault();
        },
        onOverviewTouchMove(event) {
            if (!event.touches || event.touches.length < 2) {
                return;
            }
            if (!this.pinchGesture) {
                this.onOverviewTouchStart(event);
                return;
            }
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            if (event.cancelable) event.preventDefault();
            const gesture = this.pinchGesture;
            const ratio = this.pinchDistance(event.touches) /
                gesture.startDistance;
            const nextScale = Math.max(
                0.5,
                Math.min(1.5, gesture.startScale * ratio)
            );
            const roundedScale = Math.round(nextScale * 1000) / 1000;
            const point = this.pinchPoint(event.touches, viewport);
            this.applyOverviewScale(roundedScale);
            viewport.scrollLeft = gesture.anchorX * roundedScale - point.x;
            viewport.scrollTop = gesture.anchorY * roundedScale - point.y;
        },
        onOverviewTouchEnd(event) {
            if (
                !this.pinchGesture ||
                event.touches && event.touches.length >= 2
            ) {
                return;
            }
            if (event.cancelable) event.preventDefault();
            this.pinchGesture = null;
            this.suppressTapUntil = Date.now() + 400;
        },
        onOverviewClickCapture(event) {
            if (Date.now() >= this.suppressTapUntil) return;
            event.preventDefault();
            event.stopPropagation();
        },
        applyOverviewScale(scale) {
            this.overviewScale = scale;
            const board = this.$refs.board;
            if (!board) return;
            board.style.zoom = String(scale);
            board.style.minHeight = `${100 / scale}%`;
        },
        filtered(employees) {
            return this.filterEmployees(Array.isArray(employees) ? employees : []);
        },
        plannedEmployeesForTarget(type, id) {
            const employees = this.plannedEmployeesByTarget[`${type}:${id}`];
            return this.filtered(Array.isArray(employees) ? employees : []);
        },
        plannedEmployeeCountForTarget(type, id) {
            const employees = this.plannedEmployeesByTarget[`${type}:${id}`];
            return Array.isArray(employees) ? employees.length : 0;
        },
        column(options) {
            const value = options || {};
            const employees = this.filtered(value.employees);
            const plannedEmployees = Array.isArray(value.plannedEmployees)
                ? value.plannedEmployees
                : [];
            return Object.assign({}, value, {
                employees,
                plannedEmployees,
                displayEmployees: employees.concat(plannedEmployees),
                headcount: Array.isArray(value.headcountEmployees)
                    ? value.headcountEmployees.length
                    : employees.length,
                plannedHeadcount: Number(value.plannedHeadcount) || 0,
                locationKey: value.location && value.location.layout_key || null
            });
        },
        groupCluster(group) {
            const objects = Array.isArray(group && group.objects) ? group.objects : [];
            const groupId = group.id;
            const groupKey = String(groupId);
            const objectColumns = objects.map(object => (
                this.objectColumn(object, `group:${groupId}`)
            ));
            const filledColumns = objectColumns.filter(column => (
                column.headcount > 0 || column.plannedHeadcount > 0
            ));
            const emptyColumns = objectColumns.filter(column => (
                column.headcount === 0 && column.plannedHeadcount === 0
            ));
            const emptyExpanded = this.expandedEmptyGroups[groupKey] === true;
            const columns = [
                this.column({
                    key: `group-target:${groupId}`,
                    title: 'На группе объектов',
                    employees: group.employees,
                    headcountEmployees: group.employees,
                    plannedEmployees: this.plannedEmployeesForTarget(
                        'object_group',
                        groupId
                    ),
                    plannedHeadcount: this.plannedEmployeeCountForTarget(
                        'object_group',
                        groupId
                    ),
                    target: {type: 'object_group', id: groupId},
                    canMove: this.movementAllowed,
                    allowDrop: group.can_move === true
                })
            ].concat(filledColumns);
            if (emptyColumns.length) {
                columns.push({
                    key: `empty-toggle:${groupId}`,
                    kind: 'empty-toggle',
                    groupId,
                    emptyCount: emptyColumns.length,
                    expanded: emptyExpanded
                });
                if (emptyExpanded) columns.push(...emptyColumns);
            }
            return {
                key: `group:${groupId}`,
                title: group.name || 'Группа объектов',
                icon: 'fas fa-layer-group',
                columns
            };
        },
        toggleEmptyGroupObjects(groupId) {
            const key = String(groupId);
            this.$set(
                this.expandedEmptyGroups,
                key,
                this.expandedEmptyGroups[key] !== true
            );
        },
        objectColumn(object, scopeKey) {
            const id = this.objectId(object);
            return this.column({
                key: `object:${id}`,
                title: object.name || `Объект #${id}`,
                employees: object.employees,
                headcountEmployees: object.employees,
                plannedEmployees: this.plannedEmployeesForTarget('object', id),
                plannedHeadcount: this.plannedEmployeeCountForTarget(
                    'object',
                    id
                ),
                target: {type: 'object', id},
                canMove: this.movementAllowed,
                allowDrop: object.can_move === true,
                location: object,
                scopeKey
            });
        },
        employeeKey(employee) {
            const employeeId = employee.employee_id || employee.id;
            if (employee.workforce_planned_projection === true) {
                const plannedMoveId = employee.planned_move &&
                    employee.planned_move.id || 'planned';
                return `${employeeId}:planned:${plannedMoveId}`;
            }
            const assignment = employee.assignment ||
                employee.assignment_context ||
                {};
            const assignmentId = assignment.assignment_id || assignment.id;
            const kind = assignment.kind ||
                assignment.assignment_kind ||
                (assignment.is_primary === true ? 'primary' : 'additional');
            const targetType = assignment.target_type || assignment.type || 'none';
            const targetId = assignment.target_id ||
                assignment.building_id ||
                assignment.object_group_id ||
                'common';
            return `${employeeId}:${assignmentId || `${kind}:${targetType}:${targetId}`}`;
        },
        columnCanDrop(column) {
            return !this.mobile &&
                this.movementAllowed &&
                column.allowDrop === true &&
                Boolean(column.target);
        },
        onColumnDragOver(event, column) {
            if (this.draggingEmployeeId !== null) {
                if (!this.columnCanDrop(column)) return;
                event.preventDefault();
                if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
                this.employeeDropTargetKey = column.key;
                return;
            }
            if (column.location) {
                this.$emit('location-dragover', {
                    event,
                    object: column.location,
                    scopeKey: column.scopeKey
                });
            }
        },
        onColumnDragLeave(event, column) {
            if (
                this.employeeDropTargetKey === column.key &&
                !(
                    event.currentTarget &&
                    event.relatedTarget &&
                    event.currentTarget.contains(event.relatedTarget)
                )
            ) {
                this.employeeDropTargetKey = null;
            }
            if (column.location) {
                this.$emit('location-dragleave', {
                    event,
                    object: column.location
                });
            }
        },
        onColumnDrop(event, column) {
            if (this.draggingEmployeeId !== null) {
                if (!this.columnCanDrop(column)) return;
                event.preventDefault();
                event.stopPropagation();
                this.employeeDropTargetKey = null;
                this.$emit('drop', column.target);
                return;
            }
            if (column.location) {
                this.$emit('location-drop', {
                    event,
                    object: column.location,
                    scopeKey: column.scopeKey
                });
            }
        },
        onLocationDragStart(event, column) {
            this.$emit('location-dragstart', {
                event,
                object: column.location,
                scopeKey: column.scopeKey
            });
        },
        onLocationKeydown(event, column) {
            const steps = {
                ArrowLeft: -1,
                ArrowUp: -1,
                ArrowRight: 1,
                ArrowDown: 1
            };
            if (!Object.prototype.hasOwnProperty.call(steps, event.key)) return;
            event.preventDefault();
            event.stopPropagation();
            this.$emit('location-reorder-step', {
                locationKey: column.locationKey,
                step: steps[event.key],
                scopeKey: column.scopeKey
            });
        },
        forwardContextMenu(event, employee) {
            this.$emit('contextmenu', event, employee);
        },
        onOverviewDragOver(event) {
            if (
                this.mobile ||
                (!this.draggingLocationKey && this.draggingEmployeeId === null) ||
                !this.$el
            ) {
                return;
            }
            const rect = this.$el.getBoundingClientRect();
            const edge = Math.min(90, Math.max(48, rect.width * 0.1));
            const verticalEdge = Math.min(80, Math.max(42, rect.height * 0.12));
            let left = 0;
            let top = 0;
            if (event.clientX < rect.left + edge) {
                left = -18;
            } else if (event.clientX > rect.right - edge) {
                left = 18;
            }
            if (event.clientY < rect.top + verticalEdge) {
                top = -14;
            } else if (event.clientY > rect.bottom - verticalEdge) {
                top = 14;
            }
            if (left || top) {
                this.$el.scrollBy({left, top, behavior: 'auto'});
            }
        }
    }
};
</script>

<style scoped>
.workforce-overview {
    width: 100%;
    height: calc(100vh - var(--shell-header-height, 64px) - 170px);
    height: calc(100dvh - var(--shell-header-height, 64px) - 170px);
    min-height: 300px;
    overflow: auto;
    border: 1px solid #bfcbd7;
    border-radius: 10px;
    background: #eaf0f5;
    box-shadow: 0 3px 12px rgba(35, 53, 72, 0.08);
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
    scrollbar-gutter: stable;
    touch-action: pan-x pan-y;
    -webkit-overflow-scrolling: touch;
}

.workforce-overview--pinching {
    cursor: zoom-in;
    user-select: none;
}

.workforce-overview__board {
    display: flex;
    min-width: max-content;
    min-height: 100%;
    align-items: stretch;
}

.workforce-overview__cluster {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    border-right: 2px solid #9fb0c0;
}

.workforce-overview__cluster:last-child {
    border-right: 0;
}

.workforce-overview__cluster-header {
    display: flex;
    height: 29px;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    border-bottom: 1px solid #aebdcc;
    background: #d9e5f0;
    color: #29465f;
    font-size: 0.68rem;
    font-weight: 800;
}

.workforce-overview__cluster-header span {
    max-width: calc(100vw - 40px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-overview__columns {
    display: flex;
    flex: 1 1 auto;
    align-items: stretch;
}

.workforce-overview__column {
    position: relative;
    display: flex;
    width: 176px;
    flex: 0 0 176px;
    flex-direction: column;
    min-height: 100%;
    border-right: 1px solid #cbd5de;
    background: #f5f8fb;
}

.workforce-overview__column:last-child {
    border-right: 0;
}

.workforce-overview__column--planned-object {
    border: 2px dashed #8f77c9;
    background: #faf7ff;
}

.workforce-overview__column--empty-toggle {
    width: 132px;
    flex-basis: 132px;
    align-items: stretch;
    justify-content: flex-start;
    padding: 6px;
    border-right: 1px dashed #9eb3c6;
    border-left: 1px dashed #9eb3c6;
    background: #eef4f9;
}

.workforce-overview__empty-toggle {
    display: flex;
    width: 100%;
    min-height: 72px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 5px;
    padding: 8px;
    border: 1px dashed #7599b8;
    border-radius: 7px;
    color: #28628f;
    background: rgba(255, 255, 255, 0.78);
    font-size: 0.66rem;
    font-weight: 800;
    line-height: 1.15;
    text-align: center;
}

.workforce-overview__empty-toggle i {
    font-size: 0.85rem;
}

.workforce-overview__empty-toggle small {
    display: inline-flex;
    min-width: 22px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 999px;
    color: #536b82;
    background: #dfe9f2;
    font-size: 0.61rem;
}

.workforce-overview__empty-toggle:active {
    background: #dceaf6;
}

.workforce-overview__column--drop-active {
    background: #e7f3ff;
    box-shadow: inset 0 0 0 2px #2f80c9;
}

.workforce-overview__column--location-dragging {
    opacity: 0.42;
}

.workforce-overview__column--drop-before::before,
.workforce-overview__column--drop-after::after {
    position: absolute;
    z-index: 7;
    top: 29px;
    bottom: 0;
    width: 4px;
    background: #176fbd;
    content: '';
}

.workforce-overview__column--drop-before::before {
    left: -2px;
}

.workforce-overview__column--drop-after::after {
    right: -2px;
}

.workforce-overview__column-header {
    display: grid;
    min-height: 43px;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 5px;
    padding: 6px;
    border-bottom: 1px solid #b9c7d3;
    background: #fff;
}

.workforce-overview__column-header strong,
.workforce-overview__column-header button,
.workforce-overview__location-title {
    min-width: 0;
    overflow: hidden;
    padding: 0;
    border: 0;
    background: transparent;
    color: #263c51;
    font-size: 0.68rem;
    font-weight: 800;
    line-height: 1.2;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-overview__column-header button,
.workforce-overview__location-title {
    cursor: grab;
}

.workforce-overview__column-header button:active,
.workforce-overview__location-title:active {
    cursor: grabbing;
}

.workforce-overview__counts {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: #536b82;
    font-size: 0.62rem;
    font-weight: 800;
    white-space: nowrap;
}

.workforce-overview__counts > span:first-child {
    min-width: 21px;
    padding: 2px 5px;
    border-radius: 999px;
    background: #e8eef4;
    text-align: center;
}

.workforce-overview__planned-count {
    color: #6d57a0;
}

.workforce-overview__employees {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 2px;
    padding: 4px;
}

.workforce-overview__empty {
    padding: 5px;
    color: #98a5b2;
    font-size: 0.7rem;
    text-align: center;
}

@media (max-width: 767.98px) {
    .workforce-overview {
        height: calc(
            100vh - var(--shell-header-height, 56px) -
            118px - env(safe-area-inset-bottom)
        );
        height: calc(
            100dvh - var(--shell-header-height, 56px) -
            118px - env(safe-area-inset-bottom)
        );
        min-height: 220px;
        border-radius: 7px;
    }

    .workforce-overview__column {
        width: 148px;
        flex-basis: 148px;
    }

    .workforce-overview__column--empty-toggle {
        width: 116px;
        flex-basis: 116px;
        padding: 4px;
    }

    .workforce-overview__cluster-header span {
        max-width: 260px;
    }
}
</style>
